import {getDateString} from '@shared/helpers/date'

import {getSettlementPayHold, getSettlementSummary, PayHoldType, PayHoldReasonType} from '@web/actions/saleSettlement'
import {APIStatus} from '@web/core/constants'
import {SettlementMethod, getInitialState} from '@web/slices/saleSettlement/slice'
import makeSlice from '@web/slices/saleSettlement/sliceMaker'

import {changeSaleSettlementPayHoldDate} from '../../actions/saleSettlementPayHold'

/** 지급보류 설정구분 */
const PayHoldTypeNameMap = {
    [PayHoldType.CONFIG]: '보류완료',
    [PayHoldType.RELEASE]: '보류완료 해제',
} as const

/** 지급보류 설정 사유 */
const PayHoldReasonMap = {
    [PayHoldReasonType.RM_REGIST_BOND]: '분쟁대응',
    [PayHoldReasonType.TRADEMARK_RIGHTS]: '상표권대응',
    [PayHoldReasonType.GARNISHMENT]: '채권압류',
    [PayHoldReasonType.ILLEGAL_DEAL]: '부정거래',
    [PayHoldReasonType.RESERVATION_OPERATE]: '예약운영',
    [PayHoldReasonType.HEALTH_INSURANCE]: '건강보험공단',
    [PayHoldReasonType.ETC]: '기타',
} as const

const saleSettlementSlice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(getSettlementSummary.pending, getInitialState)
            .addCase(getSettlementSummary.fulfilled, (state, {payload}) => {
                const {
                    settleCompleteTotalAmount,
                    settleCompleteTotalCount,
                    settleCompleteBasisAmount,
                    settleCompleteProductOrderAmount,
                    settleCompleteExtraFeeAmount,
                    settleCompleteDeductionRestoreAmount,
                    settleCompleteDeliveryFeeAmount,
                    settleCompleteEtcAmount,
                    settleCompleteMinusSetOffAmount,
                    settleCompletePayHoldbackAmount,
                    settleCompleteProductOrderCount,
                    settleExpectTotalAmount,
                    settleExpectTotalCount,
                    settleExpectProductOrderAmount,
                    settleExpectProductOrderCount,
                    settleMethodType,
                } = payload
                state.summary.complete = {
                    amount: settleCompleteTotalAmount,
                    count: settleCompleteTotalCount ?? settleCompleteProductOrderCount ?? 0,
                }
                state.summary.expect = {
                    amount: settleExpectTotalAmount ?? settleExpectProductOrderAmount ?? 0,
                    count: settleExpectTotalCount ?? settleExpectProductOrderCount ?? 0,
                }
                state.summary.detail = {
                    totalAmount: settleCompleteTotalAmount,
                    baseAmount: settleCompleteBasisAmount,
                    productOrderAmount: settleCompleteProductOrderAmount,
                    extraFeeAmount: settleCompleteExtraFeeAmount,
                    deductionRestoreAmount: settleCompleteDeductionRestoreAmount,
                    deliveryFeeAmount: settleCompleteDeliveryFeeAmount ?? 0,
                    etcAmount: settleCompleteEtcAmount,
                    minusSetOffAmount: settleCompleteMinusSetOffAmount,
                    payHoldBackAmount: settleCompletePayHoldbackAmount,
                }
                if (settleMethodType) {
                    state.method =
                        settleMethodType === 'CHARGE_AMT'
                            ? SettlementMethod.REFUND_AMOUNT
                            : SettlementMethod.BANK_TRANSFER
                }
            })
            .addCase(getSettlementPayHold.pending, (state) => {
                state.payHold.status = APIStatus.FETCHING
                state.payHold.items = []
                state.payHold.totalAmount = 0
            })
            .addCase(getSettlementPayHold.fulfilled, (state, {payload}) => {
                const {payHolds, totalAmount} = payload

                state.payHold.status = APIStatus.RESOLVED
                state.payHold.items = payHolds
                    .filter(
                        (
                            item,
                        ): item is ItemType<typeof payHolds> & {
                            configReleaseType: typeof PayHoldType.CONFIG | typeof PayHoldType.RELEASE
                        } =>
                            item.configReleaseType === PayHoldType.CONFIG ||
                            item.configReleaseType === PayHoldType.RELEASE,
                    )
                    .map(({holdConfigReleaseNo, completeYmd, configReleaseType, amount, holdReasonType}) => {
                        return {
                            id: holdConfigReleaseNo,
                            date: getDateString({date: completeYmd}),
                            type: PayHoldTypeNameMap[configReleaseType],
                            reason: PayHoldReasonMap[holdReasonType],
                            amount,
                        }
                    })
                state.payHold.totalAmount = totalAmount
            })
            .addCase(getSettlementPayHold.rejected, (state) => {
                state.payHold.status = APIStatus.REJECTED
            })
            .addCase(changeSaleSettlementPayHoldDate.fulfilled, (state, {payload}) => {
                state.payHold.selected.startDate = payload.startDate
                state.payHold.selected.endDate = payload.endDate
            })
    },
})

export default saleSettlementSlice.reducer
